import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/AudioRecorderControls.css";

const useAudioDevices = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasPermission, setHasPermission] = useState(false);

  const normalizeDevices = useCallback((deviceList) => {
    return deviceList
      .filter((device) => device.kind === "audioinput")
      .map((device) => {
        const baseLabel = device.label || `Microphone ${device.deviceId.slice(0, 8)}`;
        return {
          deviceId: device.deviceId,
          label: baseLabel.replace(/\s*\([^)]*\)/g, "").trim(),
          groupId: device.groupId,
        };
      });
  }, []);

  const loadDevices = useCallback(
    async ({ requestPermission } = { requestPermission: false }) => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        setError("Media devices not supported");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (requestPermission) {
          const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
          tempStream.getTracks().forEach((track) => track.stop());
          setHasPermission(true);
        }

        const deviceList = await navigator.mediaDevices.enumerateDevices();
        setDevices(normalizeDevices(deviceList));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to get audio devices");
      } finally {
        setLoading(false);
      }
    },
    [normalizeDevices]
  );

  useEffect(() => {
    loadDevices({ requestPermission: false });
  }, [loadDevices]);

  useEffect(() => {
    const handleDeviceChange = () => {
      loadDevices({ requestPermission: hasPermission });
    };

    navigator.mediaDevices?.addEventListener("devicechange", handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener("devicechange", handleDeviceChange);
    };
  }, [hasPermission, loadDevices]);

  return {
    devices,
    loading,
    error,
    hasPermission,
    loadDevices,
  };
};

const AudioLevelMeter = ({ deviceId, active }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    const stop = () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
      if (canvasRef.current) {
        const ctx = canvasRef.current.getContext("2d");
        if (ctx) {
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
    };

    const draw = () => {
      const canvas = canvasRef.current;
      const analyser = analyserRef.current;
      if (!canvas || !analyser) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      analyser.getByteFrequencyData(dataArray);

      const barCount = 14;
      const barWidth = Math.max(2, Math.floor(canvas.width / (barCount * 1.6)));
      const gap = Math.max(1, Math.floor(barWidth * 0.6));
      const maxBarHeight = Math.max(2, canvas.height - 4);
      const centerY = Math.floor(canvas.height / 2);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = "#7d7d7d";

      for (let i = 0; i < barCount; i += 1) {
        const start = Math.floor((i / barCount) * bufferLength);
        const end = Math.floor(((i + 1) / barCount) * bufferLength);
        let sum = 0;
        for (let j = start; j < end; j += 1) {
          sum += dataArray[j];
        }
        const avg = sum / Math.max(1, end - start);
        const normalized = Math.min(1, avg / 180);
        const barHeight = Math.max(2, normalized * maxBarHeight);
        const x = i * (barWidth + gap);
        const y = centerY - Math.floor(barHeight / 2);
        ctx.fillRect(x, y, barWidth, barHeight);
      }

      animationRef.current = requestAnimationFrame(draw);
    };

    const start = async () => {
      if (!active) {
        stop();
        return;
      }

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: deviceId ? { deviceId: { exact: deviceId } } : true,
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 512;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        draw();
      } catch (err) {
        stop();
      }
    };

    start();

    return () => {
      cancelled = true;
      stop();
    };
  }, [active, deviceId]);

  return <canvas ref={canvasRef} className="audio-waveform-canvas" width={120} height={26} />;
};

export default function AudioRecorderControls({
  isRecording,
  recordDisabled,
  deleteDisabled,
  timerText,
  onRecordToggle,
  onDelete,
  selectedDeviceId,
  onDeviceChange,
  micDisabled,
}) {
  const { t } = useTranslation();
  const { devices, loading, error, hasPermission, loadDevices } = useAudioDevices();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!selectedDeviceId && devices.length > 0) {
      onDeviceChange?.(devices[0].deviceId);
    }
  }, [devices, onDeviceChange, selectedDeviceId]);

  useEffect(() => {
    if (!isDropdownOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const currentDevice = useMemo(() => {
    if (devices.length === 0) return null;
    return devices.find((device) => device.deviceId === selectedDeviceId) || devices[0];
  }, [devices, selectedDeviceId]);

  const statusText = isRecording
    ? timerText || t("audioControls.recording")
    : t("audioControls.startRecording");

  const handleDropdownToggle = async () => {
    const nextOpen = !isDropdownOpen;
    setIsDropdownOpen(nextOpen);
    if (nextOpen && !hasPermission && !loading) {
      await loadDevices({ requestPermission: true });
    }
  };

  return (
    <div className="audio-controls">
      <div className="audio-recorder-bar">
        <button
          type="button"
          className={`audio-recorder-status ${isRecording ? "is-recording" : ""}`}
          onClick={onRecordToggle}
          disabled={recordDisabled}
        >
          {statusText}
        </button>

        <div className="audio-recorder-divider" />

        <div className="audio-recorder-mic" ref={dropdownRef}>
          <button
            type="button"
            className={`mic-selector-trigger ${micDisabled ? "is-disabled" : ""}`}
            onClick={handleDropdownToggle}
            disabled={micDisabled || loading}
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
          >
            <span className="mic-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 14a3 3 0 0 0 3-3V5a3 3 0 1 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2z" />
              </svg>
            </span>
            <span className="mic-label">
              {loading && t("audioControls.loading")}
              {!loading && (currentDevice?.label || t("audioControls.noMicrophone"))}
            </span>
            <span className={`mic-chevron ${isDropdownOpen ? "is-open" : ""}`} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 10l5 5 5-5H7z" />
              </svg>
            </span>
          </button>

          {isDropdownOpen && (
            <div className="mic-dropdown" role="listbox">
              <div className="mic-dropdown-list">
                {error && <div className="mic-dropdown-item is-disabled">Error: {error}</div>}
                {!error && devices.length === 0 && (
                  <div className="mic-dropdown-item is-disabled">{t("audioControls.noMicrophone")}</div>
                )}
                {!error &&
                  devices.map((device) => (
                    <button
                      type="button"
                      key={device.deviceId}
                      className="mic-dropdown-item"
                      onClick={() => {
                        onDeviceChange?.(device.deviceId);
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className="mic-item-label">{device.label}</span>
                      {selectedDeviceId === device.deviceId && (
                        <span className="mic-item-check" aria-hidden="true">
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M9.5 16.2l-3.7-3.7L4.4 14l5.1 5.1L20 8.6l-1.4-1.4z" />
                          </svg>
                        </span>
                      )}
                    </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="audio-recorder-waveform" aria-hidden="true">
          <AudioLevelMeter deviceId={selectedDeviceId} active={isRecording} />
        </div>

        <div className="audio-recorder-actions">
          <button
            type="button"
            className={`audio-recorder-action audio-recorder-record ${isRecording ? "is-recording" : ""}`}
            onClick={onRecordToggle}
            disabled={recordDisabled}
            aria-label={isRecording ? t("audioControls.submit") : t("audioControls.startRecording")}
          >
            <span className="record-dot" />
          </button>
          <button
            type="button"
            className="audio-recorder-action audio-recorder-delete"
            onClick={onDelete}
            disabled={deleteDisabled}
            aria-label={t("audioControls.delete")}
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
